import { useState } from 'react'
import { router } from '@inertiajs/react'
import { message } from 'antd'
import { api } from '@/lib/apiClient'
import type { UploadFile } from 'antd/es/upload/interface'
import type { Attendee, AttendeeStatus } from '@/pages/events/attendees/types'

interface UseAttendeeActionsConfig {
  onAttendeeUpdate?: (attendeeId: number, updates: Partial<Attendee>) => void
}

export const useAttendeeActions = (
  eventSlug: string,
  attendees: Attendee[],
  config?: UseAttendeeActionsConfig
) => {
  const [profileDrawerVisible, setProfileDrawerVisible] = useState(false)
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(null)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [attendeeToDelete, setAttendeeToDelete] = useState<{ id: number; full_name: string } | null>(null)
  const [editDrawerVisible, setEditDrawerVisible] = useState(false)
  const [editingAttendee, setEditingAttendee] = useState<Attendee | null>(null)

  const handleViewProfile = (attendeeId: number) => {
    const attendee = attendees.find(a => a.id === attendeeId)
    if (attendee) {
      setSelectedAttendee(attendee)
      setProfileDrawerVisible(true)
    }
  }

  const handleEditAttendee = (attendeeId: number) => {
    const attendee = attendees.find(a => a.id === attendeeId)
    if (attendee) {
      setEditingAttendee(attendee)
      setEditDrawerVisible(true)
    }
  }

  const handleUpdateAttendee = async (values: any) => {
    if (!editingAttendee) return

    const formData = new FormData()
    const answers = values.answers || {}

    Object.entries(answers).forEach(([key, val]) => {
      if (val === undefined || val === null) return

      if (val && typeof val === 'object' && ('signed_id' in (val as any) || 'blob_id' in (val as any))) {
        const signedId = (val as any).signed_id
        const blobId = (val as any).blob_id
        formData.append(`attendee[answers][${key}]`, (signedId || blobId) as any)
        return
      }

      if (Array.isArray(val) && val.length > 0 && (val[0] as UploadFile)?.originFileObj) {
        const file = (val[0] as UploadFile).originFileObj as File | undefined
        if (file) formData.append(`attendee[answers][${key}]`, file)
        return
      }

      if (Array.isArray(val)) {
        val.forEach((v) => formData.append(`attendee[answers][${key}][]`, v))
        return
      }

      formData.append(`attendee[answers][${key}]`, val as any)
    })

    if (answers.email) formData.append('attendee[email]', answers.email as any)
    if (answers.full_name || answers.name) formData.append('attendee[full_name]', (answers.full_name || answers.name) as any)
    if (answers.phone || answers.contact_number) formData.append('attendee[phone]', (answers.phone || answers.contact_number) as any)

    try {
      await api.patch(`/events/${eventSlug}/attendees/${editingAttendee.id}`, formData)
      message.success('Attendee updated successfully')
      setEditDrawerVisible(false)
      setEditingAttendee(null)
      router.reload()
    } catch (error: any) {
      const apiErrors = error?.data?.errors
      if (Array.isArray(apiErrors)) {
        message.error(apiErrors.join(', '))
      } else {
        message.error(error?.message || 'Failed to update attendee')
      }
    }
  }

  const closeEditDrawer = () => {
    setEditDrawerVisible(false)
    setEditingAttendee(null)
  }

  const handleDeleteClick = (attendeeId: number, attendeeFullName: string) => {
    setAttendeeToDelete({ id: attendeeId, full_name: attendeeFullName })
    setDeleteModalVisible(true)
  }

  const handleDeleteAttendee = () => {
    if (!attendeeToDelete) return

    router.delete(`/events/${eventSlug}/attendees/${attendeeToDelete.id}`, {
      onSuccess: () => {
        setDeleteModalVisible(false)
        setAttendeeToDelete(null)
      },
      onError: (errors: any) => {
        // Handle errors from backend
        if (errors?.errors && Array.isArray(errors.errors)) {
          message.error(errors.errors.join(', '))
        } else {
          message.error('Failed to delete attendee')
        }
      },
    })
  }

  // Generic status action handler to reduce code duplication
  const handleStatusAction = async (
    attendeeId: number,
    action: 'approve' | 'reject' | 'cancel',
    successMessage: string,
    fallbackStatus: AttendeeStatus
  ) => {
    try {
      const response = await api.post<{ success: boolean; status: AttendeeStatus }>(
        `/events/${eventSlug}/attendees/${attendeeId}/${action}`
      )
      message.success(successMessage)

      const newStatus = response.status || fallbackStatus

      // Update selectedAttendee in drawer immediately
      setSelectedAttendee(prev =>
        prev?.id === attendeeId ? { ...prev, status: newStatus } : prev
      )

      // Update the attendees list via callback
      config?.onAttendeeUpdate?.(attendeeId, { status: newStatus })
    } catch (error: any) {
      message.error(error?.data?.error || `Failed to ${action} registration`)
    }
  }

  const handleApprove = (attendeeId: number) =>
    handleStatusAction(attendeeId, 'approve', 'Registration approved', 'approved')

  const handleReject = (attendeeId: number) =>
    handleStatusAction(attendeeId, 'reject', 'Registration rejected', 'rejected')

  const handleCancel = (attendeeId: number) =>
    handleStatusAction(attendeeId, 'cancel', 'Registration cancelled', 'cancelled')

  return {
    profileDrawerVisible,
    setProfileDrawerVisible,
    selectedAttendee,
    setSelectedAttendee,
    deleteModalVisible,
    setDeleteModalVisible,
    attendeeToDelete,
    setAttendeeToDelete,
    editDrawerVisible,
    setEditDrawerVisible,
    editingAttendee,
    setEditingAttendee,
    handleViewProfile,
    handleEditAttendee,
    handleUpdateAttendee,
    closeEditDrawer,
    handleDeleteClick,
    handleDeleteAttendee,
    handleApprove,
    handleReject,
    handleCancel,
  }
}
